import React, { Component, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import moment from 'moment'
import { Button, Input } from 'semantic-ui-react'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import 'react-day-picker/lib/style.css'
import classnames from 'classnames/bind'
import { UI_DATE_FORMAT } from '../../../constants/statics'
import { commonPropTypes, formFieldWrapper } from '../formFieldWrapper'
import styles from '../styles.scss'
import YearMonthForm from './yearMonthForm'
import getWrappedMaskedInput from './getWrappedMaskedInput'
const cx = classnames.bind(styles)

/**
 * DayPickerInput calendar overlay positioned `fixed` so it escapes any ancestor
 * `overflow` (e.g. the filter panel's scroll container) that would clip it,
 * without portaling (portaling broke day selection). Measures the input and,
 * because `fixed` is relative to the nearest transformed ancestor (a Popper
 * popup) rather than the viewport, offsets by that ancestor's box.
 */
const FixedDatePickerOverlay = ({
  classNames,
  orientation,
  children,
  elmId,
  ...rest
}) => {
  const overlayRef = useRef(null)
  const [style, setStyle] = useState({ position: 'fixed', zIndex: 2000 })

  useLayoutEffect(() => {
    const inputEl = overlayRef.current
      ?.closest('.ui.input')
      ?.querySelector('input')
    if (!inputEl) return
    const inputRect = inputEl.getBoundingClientRect()
    let containingBlock = overlayRef.current.parentElement
    while (
      containingBlock &&
      containingBlock !== document.body &&
      window.getComputedStyle(containingBlock).transform === 'none'
    ) {
      containingBlock = containingBlock.parentElement
    }
    const containingBlockRect =
      containingBlock && containingBlock !== document.body
        ? containingBlock.getBoundingClientRect()
        : { top: 0, left: 0, bottom: window.innerHeight }
    setStyle({
      position: 'fixed',
      zIndex: 2000, // above Semantic's Popup (1900)
      left: inputRect.left - containingBlockRect.left,
      ...(orientation === 'top'
        ? { bottom: containingBlockRect.bottom - inputRect.top }
        : { top: inputRect.bottom - containingBlockRect.top }),
    })
  }, [orientation])

  return (
    <div
      ref={overlayRef}
      className={classNames.overlayWrapper}
      style={style}
      {...rest}
      data-elm-id={elmId ? `${elmId}_daypicker_popup` : undefined}
    >
      <div className={classNames.overlay}>{children}</div>
    </div>
  )
}

/**
 * InputWrapper is needed so we can hold onto a unique reference to the dayPickerInput and track
 * the unparsedDate - just for this instance
 */
class InputWrapper extends Component {
  static propTypes = {
    ...commonPropTypes,
    showError: PropTypes.bool.isRequired,
    compact: PropTypes.bool,
    modifiable: PropTypes.bool,
    orientation: PropTypes.oneOf(['top', 'bottom']),
    disabledDays: PropTypes.object,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
  }

  static defaultProps = {
    compact: false,
    modifiable: false,
    orientation: 'bottom',
  }

  constructor(props) {
    super(props)
    this.dayPickerInput = undefined
    this.unparsedDate = ''
    this.state = {
      month: new Date(),
    }
  }

  get innerInput() {
    return _.get(this.dayPickerInput, 'input.input.inputElement', {})
  }

  /**
   *  Sample usage of disabledDays, used to disable parts of calendar selection
   *  See: https://react-day-picker.js.org/api/DayPicker#disabledDays
   *   <DatePickerField
   *     disabledDays={{
   *       before: new Date(2020, 7, 1)
   *     }}
   *   />
   */
  get dayPickerProps() {
    const { modifiable, disabledDays } = this.props
    const modifiableProps = {
      month: this.state.month,
      captionElement: this.renderCaptionElement,
    }

    const dayPickerProps = {}

    if (modifiable) {
      _.merge(dayPickerProps, modifiableProps)
    }
    if (disabledDays) {
      _.merge(dayPickerProps, { disabledDays })
    }
    return dayPickerProps
  }

  formatDate = (date, format) => moment(date).format(format)

  isValidDate = (s, format) => moment(s, format, true).isValid()

  /**
   * Converts a string to a JS Date object, only if it's a valid date.
   */
  parseDate = (s, format) => {
    this.unparsedDate = s
    const dateMoment = moment(s, format, true)
    return dateMoment.isValid() ? dateMoment.toDate() : undefined
  }

  /**
   * Puts focus on the DayPickerInput's inner input, at a specific position if passed.
   */
  focusInput = (pos = 0) => {
    const input = this.innerInput
    if (input && input.focus) {
      input.focus()
      input.selectionStart = pos
      input.selectionEnd = pos
    }
  }

  /**
   * Called when user types into input or clicks on a day.
   * The underlying value is a string version of the date and CAN be a partially entered date
   * e.g. '01/__/____' - so that it behaves like a date masked input.
   */
  onDayChange = val => {
    // our underlying value is just a string (not date obj)
    const { format, onChange } = this.props
    if (typeof onChange !== 'function') {
      return
    }

    // only call onChange if date is valid to avoid unfocusing the input on mobile for each character typed
    if (this.isValidDate(val, format)) {
      onChange(this.formatDate(val, format))
    }

    /**
     * The following is necessary due to a flaw in the DayPickerInput where it loses focus when
     * you change the value to an invalid date value.  It attempts to find the date in the calendar
     * picker, fails but then loses focus on the input.
     */
    const pos = this.innerInput.selectionStart
    _.defer(() => {
      if (!this.innerInput.value) {
        /**
         * This is necessary because parseDate does not get called when value is cleared so we don't
         * get the latest unparsedDate in that case.  So instead, we check and ensure redux input
         * is properly set when date is empty string or undefined
         */
        onChange('')
      }
      this.focusInput(pos)
    })
  }

  handleYearMonthChange = month => {
    this.setState({ month })
  }

  renderCaptionElement = ({ date, localeUtils }) => {
    const { elmId } = this.props
    return (
      <YearMonthForm
        elmId={elmId}
        date={date}
        localeUtils={localeUtils}
        onChange={this.handleYearMonthChange}
      />
    )
  }

  // Render the calendar via FixedDatePickerOverlay so it escapes overflow
  // clipping while staying inline (preserves day-click/focus handling). `rest`
  // carries onFocus/onBlur/tabIndex — must reach the wrapper or it closes on open.
  renderOverlay = ({
    input, // eslint-disable-line no-unused-vars
    month, // eslint-disable-line no-unused-vars
    selectedDay, // eslint-disable-line no-unused-vars
    ...rest
  }) => (
    <FixedDatePickerOverlay
      orientation={this.props.orientation}
      elmId={this.props.elmId}
      {...rest}
    />
  )

  /**
   * Here we render a semantic Input that has a child of a DayPickerInput, which in turn takes
   * the WrappedMaskedInput as it's component.  The WrappedMaskedInput is necessary to support
   * a "focus" method which it doesn't otherwise provide but is required by DayPicker.  The
   * WrappedMaskedInput in turn renders the MaskedInput for masked date entry
   */
  render() {
    const {
      disabled,
      elmId,
      value,
      label, // eslint-disable-line
      placeholder,
      showError,
      format,
      className,
      compact,
      modifiable, // eslint-disable-line
      onBlur,
      onFocus,
      disabledDays, // pulling his out of ...rest so it doesn't get passed to Input
      ...rest
    } = this.props
    const newClassName = classnames({
      compact,
      [className]: true,
    })

    return (
      <Input
        error={showError}
        actionPosition='left'
        className={`${cx('datepicker')} ${newClassName}`}
        data-elm-id={`${elmId}_datepicker`}
        disabled={!!disabled}
        onFocus={onFocus}
        {...rest}
      >
        <Button
          icon='calendar alternate'
          onClick={this.focusInput}
          disabled={!!disabled}
          as='a'
        />
        <DayPickerInput
          ref={el => (this.dayPickerInput = el)}
          value={value}
          onDayChange={this.onDayChange}
          format={format}
          formatDate={this.formatDate}
          parseDate={this.parseDate}
          placeholder={format}
          keepFocus
          dayPickerProps={this.dayPickerProps}
          overlayComponent={this.renderOverlay}
          classNames={{
            overlay: '',
            overlayWrapper: cx('datepicker-overlay-wrapper'),
            container: cx('datepicker-container', 'datepicker-input'),
          }}
          component={getWrappedMaskedInput(placeholder, !!disabled)}
          inputProps={{ name: rest.name, onBlur }}
        />
      </Input>
    )
  }
}

const DatePickerField = formFieldWrapper((fieldProps, showError) => (
  <InputWrapper
    {...fieldProps}
    showError={showError}
    format={fieldProps.placeholder || UI_DATE_FORMAT}
  />
))

DatePickerField.propTypes = commonPropTypes

export default DatePickerField
